import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowRight, Brain, Target, Zap, CheckCircle, BarChart3, TrendingUp, Lightbulb } from 'lucide-react'

const analysisTools = [
  {
    name: 'Pros & Cons Analysis',
    path: '/decision-analysis/pros-cons',
    desc: 'Analyze pros, cons, risks and opportunities with AI',
    icon: '⚖️',
    features: ['Detailed pros/cons', 'Risk assessment', 'Next steps', 'Confidence scoring'],
    color: 'from-blue-500 to-blue-600'
  },
  {
    name: 'Causal Chain Analysis',
    path: '/decision-analysis/causal-chain',
    desc: 'Show decision outcomes in causal chains',
    icon: '🔗',
    features: ['Causal mapping', 'Impact analysis', 'Long-term effects', 'Risk assessment'],
    color: 'from-green-500 to-green-600'
  },
  {
    name: 'Context Analysis',
    path: '/decision-analysis/context',
    desc: 'Get personalized advice based on your context',
    icon: '🎯',
    features: ['Personalized insights', 'Context-aware advice', 'Situational analysis', 'Custom recommendations'],
    color: 'from-purple-500 to-purple-600'
  },
  {
    name: 'Multi-Option Analysis',
    path: '/decision-analysis/multi-option',
    desc: 'Compare multiple options systematically',
    icon: '📊',
    features: ['Systematic comparison', 'Scoring matrix', 'Weighted analysis', 'Option ranking'],
    color: 'from-orange-500 to-orange-600'
  }
]

export const AnalysisLandingPage: React.FC = () => {
  const navigate = useNavigate()

  return (
    <>
      <Helmet>
        <title>AI Decision Analysis - Get Intelligent Insights for Better Decisions</title>
        <meta name="description" content="Use our AI analysis tools to get intelligent insights, pros/cons analysis, and personalized recommendations for any decision. Free AI-powered decision analysis." />
        <meta name="keywords" content="AI decision analysis, pros and cons analysis, decision making tools, AI insights, risk assessment" />
        <meta property="og:title" content="AI Decision Analysis - Get Intelligent Insights for Better Decisions" />
        <meta property="og:description" content="Use our AI analysis tools to get intelligent insights and personalized recommendations for any decision." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://aidecision.com/analysis" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Decision Analysis - Get Intelligent Insights for Better Decisions" />
        <meta name="twitter:description" content="Use our AI analysis tools to get intelligent insights and personalized recommendations for any decision." />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "AI Decision Analysis",
            "description": "Get intelligent insights and personalized recommendations for any decision using AI analysis tools.",
            "url": "https://aidecision.com/analysis",
            "applicationCategory": "ProductivityApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Pros and Cons Analysis",
              "Causal Chain Analysis", 
              "Context Analysis",
              "Multi-Option Analysis"
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-extrabold text-primary-700 mb-6">
              AI Decision Analysis
            </h1>
            <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
              Get intelligent insights and personalized recommendations for any decision in seconds.
            </p>
            
            {/* Quick Benefits */}
            <div className="mb-12 max-w-3xl mx-auto">
              <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="text-2xl mb-2">⚡</div>
                    <div className="font-semibold text-gray-900">3-Second Analysis</div>
                    <div className="text-sm text-gray-600">Instant AI insights</div>
                  </div>
                  <div>
                    <div className="text-2xl mb-2">🎯</div>
                    <div className="font-semibold text-gray-900">Personalized Advice</div>
                    <div className="text-sm text-gray-600">Context-aware recommendations</div>
                  </div>
                  <div>
                    <div className="text-2xl mb-2">🔒</div>
                    <div className="font-semibold text-gray-900">100% Free</div>
                    <div className="text-sm text-gray-600">No hidden costs</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Analysis Tools Section */}
        <section className="py-8 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Choose Your Analysis Type</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Select the analysis method that best fits your decision-making needs.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {analysisTools.map(tool => (
                <div 
                  key={tool.path} 
                  className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300 cursor-pointer group"
                  onClick={() => navigate(tool.path)}
                >
                  <div className="text-center mb-6">
                    <div className="text-5xl mb-4">{tool.icon}</div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{tool.name}</h3>
                    <p className="text-gray-600 mb-6">{tool.desc}</p>
                  </div>
                  
                  <div className="space-y-3 mb-8">
                    {tool.features.map((feature, index) => (
                      <div key={index} className="flex items-center text-gray-700">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <button 
                    className={`w-full py-4 px-6 bg-gradient-to-r ${tool.color} text-white font-semibold rounded-lg transition-all duration-300 group-hover:scale-105 flex items-center justify-center space-x-2`}
                  >
                    <span>Start Analysis</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">How AI Analysis Works</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Brain className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">1. Describe Your Decision</h3>
                <p className="text-gray-600">Tell us about your decision, options, and any relevant context.</p>
              </div>
              
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2. AI Analysis</h3>
                <p className="text-gray-600">Our AI analyzes your decision using advanced algorithms and provides insights.</p>
              </div>
              
              <div className="text-center">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">3. Get Recommendations</h3>
                <p className="text-gray-600">Receive detailed analysis, pros/cons, and actionable recommendations.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Perfect For Every Decision</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-6 shadow-lg border border-gray-100">
                <div className="flex items-center mb-4">
                  <BarChart3 className="w-8 h-8 text-blue-600 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900">Career Decisions</h3>
                </div>
                <p className="text-gray-600 text-sm">Job changes, career paths, skill development decisions.</p>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-lg border border-gray-100">
                <div className="flex items-center mb-4">
                  <TrendingUp className="w-8 h-8 text-green-600 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900">Financial Choices</h3>
                </div>
                <p className="text-gray-600 text-sm">Investments, purchases, financial planning decisions.</p>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-lg border border-gray-100">
                <div className="flex items-center mb-4">
                  <Lightbulb className="w-8 h-8 text-yellow-600 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900">Life Choices</h3>
                </div>
                <p className="text-gray-600 text-sm">Personal decisions, relationships, lifestyle changes.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Make Better Decisions?</h2>
              <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                Start with our most popular analysis tool and get instant insights for your decision.
              </p>
              <button 
                onClick={() => navigate('/decision-analysis/pros-cons')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 hover:scale-105 flex items-center space-x-2 mx-auto"
              >
                <span>Start Pros & Cons Analysis</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </section>
      </div>
    </>
  )
} 